import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Search, Gauge, Type, MousePointerClick, Palette, Megaphone,
  Sparkles, Loader2, RotateCcw, ArrowRight, CheckCircle2, AlertTriangle,
  Lock, Download, Mail,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const PILLAR_META = {
  seo: { icon: Search },
  speed: { icon: Gauge },
  copy: { icon: Type },
  cta: { icon: MousePointerClick },
  brand: { icon: Palette },
  ads: { icon: Megaphone },
};

function scoreTone(score) {
  if (score >= 80) return { label: "Strong", color: "text-emerald-400" };
  if (score >= 60) return { label: "Solid", color: "text-cyan-300" };
  if (score >= 40) return { label: "Needs work", color: "text-amber-400" };
  return { label: "Critical", color: "text-rose-400" };
}

function Dial({ score }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(100, Math.max(0, score)) / 100);
  return (
    <div className="relative h-32 w-32">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <defs>
          <linearGradient id="dial-grad" x1="0" y1="0" x2="100" y2="100">
            <stop offset="0%" stopColor="#00E5FF" />
            <stop offset="60%" stopColor="#9D4CDD" />
            <stop offset="100%" stopColor="#FF4DD8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r={r} stroke="url(#dial-grad)" strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-clash text-3xl font-semibold text-white">{score}</div>
        <div className="text-[9px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500">/ 100</div>
      </div>
    </div>
  );
}

function PillarBar({ score }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(2, score)}%` }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="h-full bg-gradient-to-r from-cyan-400 via-purple-500 to-fuchsia-500"
      />
    </div>
  );
}

export default function InstantAudit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Unlock state
  const [unlocked, setUnlocked] = useState(false);
  const [unlockData, setUnlockData] = useState(null); // { pillars, quick_wins, pdf_url, email }
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    const cleaned = url.trim();
    if (!cleaned || !/[a-zA-Z]/.test(cleaned)) {
      toast.error("Enter a valid website URL.");
      return;
    }
    setLoading(true);
    setResult(null);
    setUnlocked(false);
    setUnlockData(null);
    try {
      const res = await axios.post(`${API}/audit`, { url: cleaned }, { timeout: 75000 });
      setResult(res.data);
      toast.success("Audit ready — see your scores below.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not complete audit. Try a different URL.";
      toast.error(typeof detail === "string" ? detail : "Audit failed.");
    } finally {
      setLoading(false);
    }
  };

  const onUnlock = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanEmail)) {
      toast.error("Enter a valid work email.");
      return;
    }
    if (!result) return;
    setUnlockLoading(true);
    try {
      const res = await axios.post(`${API}/audit/unlock`, {
        audit_id: result.audit_id,
        email: cleanEmail,
        name: name.trim() || undefined,
      });
      setUnlockData({ ...res.data, email: cleanEmail });
      setUnlocked(true);
      toast.success("Unlocked. Your full audit + PDF are ready below.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not unlock. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Unlock failed.");
    } finally {
      setUnlockLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setUrl("");
    setUnlocked(false);
    setUnlockData(null);
    setEmail("");
    setName("");
  };

  // Use unlocked pillar data when available, else use preview
  const displayPillars =
    unlocked && unlockData ? unlockData.pillars : result?.pillars || [];
  const displayQuickWins =
    unlocked && unlockData ? unlockData.quick_wins : [];

  const pdfHref =
    unlocked && unlockData
      ? `${BACKEND}${unlockData.pdf_url}`
      : null;

  return (
    <section
      id="audit"
      data-testid="instant-audit-section"
      className="relative py-14 md:py-32 overflow-hidden border-y border-white/5 bg-[#070709]"
    >
      <div className="aurora" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: copy + form */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-[10px] font-mono-acc uppercase tracking-[0.28em] text-cyan-300 mb-4">
              <Sparkles size={11} className="text-purple-300" />
              Instant AI Audit · Free
            </div>
            <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Get a <span className="text-gradient-neon">6-point growth audit</span> of your website in 60 seconds.
            </h2>
            <p className="mt-5 text-zinc-400 text-base leading-relaxed max-w-md">
              QUASAR scans your site and grades SEO, speed, copy, CTA, brand and ad-readiness — plus the 3 quick wins you can ship this week.
            </p>

            <form
              onSubmit={onSubmit}
              className="mt-8 rounded-2xl glass-strong p-2 flex flex-col sm:flex-row gap-2 max-w-lg"
              data-testid="audit-url-form"
            >
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="yourwebsite.com"
                className="flex-1 bg-transparent text-white placeholder:text-zinc-500 px-5 py-3.5 outline-none text-sm rounded-xl"
                data-testid="audit-url-input"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black px-6 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform disabled:opacity-60 disabled:hover:scale-100"
                data-testid="audit-run-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Auditing…
                  </>
                ) : (
                  <>
                    Run Free Audit
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-cyan-300" /> No signup to see scores</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-cyan-300" /> Email gates full report + PDF</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-cyan-300" /> Powered by QUASAR AI</span>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg">
              {Object.entries({
                seo: "SEO", speed: "Speed", copy: "Copy",
                cta: "CTA", brand: "Brand", ads: "Ad-readiness",
              }).map(([k, lbl]) => {
                const Icon = PILLAR_META[k].icon;
                return (
                  <div key={k} className="flex items-center gap-2 rounded-xl glass px-3 py-2.5 text-xs text-zinc-300">
                    <Icon size={13} className="text-cyan-300" />
                    {lbl}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: results / placeholder */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {!result && !loading && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="rounded-3xl glass-strong p-8 md:p-10 min-h-[420px] flex flex-col items-center justify-center text-center"
                  data-testid="audit-empty-state"
                >
                  <div className="relative h-16 w-16 mb-4">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-400/20 to-purple-500/20 blur-xl" />
                    <div className="relative h-full w-full rounded-2xl glass-strong flex items-center justify-center glow-purple">
                      <Search size={22} className="text-cyan-300" />
                    </div>
                  </div>
                  <h3 className="font-clash text-xl font-medium text-white">Your audit will appear here</h3>
                  <p className="mt-2 text-sm text-zinc-400 max-w-sm">
                    Paste any URL on the left and we'll return your overall growth score plus a breakdown across 6 pillars.
                  </p>
                </motion.div>
              )}

              {loading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="rounded-3xl glass-strong p-8 md:p-10 min-h-[420px] flex flex-col items-center justify-center text-center"
                  data-testid="audit-loading-state"
                >
                  <div className="relative h-16 w-16 mb-5">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400/30 to-purple-500/30 blur-2xl animate-pulse" />
                    <div className="relative h-full w-full rounded-full glass-strong flex items-center justify-center">
                      <Loader2 size={22} className="text-cyan-300 animate-spin" />
                    </div>
                  </div>
                  <h3 className="font-clash text-xl font-medium text-white">QUASAR is analyzing your site…</h3>
                  <div className="mt-5 space-y-2 text-sm text-zinc-400">
                    {[
                      "Fetching page & rendering metadata",
                      "Scanning SEO + speed signals",
                      "Reading copy, CTAs, brand cues",
                      "Scoring across 6 growth pillars",
                    ].map((t, i) => (
                      <motion.div
                        key={t}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.4 }}
                        className="flex items-center justify-center gap-2"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 pulse-dot" />
                        {t}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {result && !loading && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="rounded-3xl glass-strong p-6 md:p-8"
                  data-testid="audit-result"
                >
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono-acc uppercase tracking-[0.28em] text-cyan-400 mb-1">
                        Audit · {(() => { try { return new URL(result.final_url).hostname; } catch { return result.url; } })()}
                      </div>
                      <p className="font-clash text-lg md:text-xl font-medium text-white leading-snug">
                        {result.headline}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <Dial score={result.overall_score} />
                      <button
                        onClick={reset}
                        className="inline-flex items-center gap-1.5 rounded-full glass px-3 py-2 text-xs text-zinc-300 hover:bg-white/10 transition-colors"
                        data-testid="audit-reset"
                      >
                        <RotateCcw size={12} /> New
                      </button>
                    </div>
                  </div>

                  {/* Pillars grid */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    {displayPillars.map((p, i) => {
                      const Icon = PILLAR_META[p.key]?.icon || Search;
                      const tone = scoreTone(p.score);
                      return (
                        <motion.div
                          key={p.key}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="rounded-2xl glass p-4 hover:bg-white/[0.04] transition-colors"
                          data-testid={`audit-pillar-${p.key}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="inline-flex items-center gap-2">
                              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-cyan-400/15 to-purple-500/15 border border-white/10 flex items-center justify-center">
                                <Icon size={13} className="text-cyan-300" />
                              </div>
                              <div>
                                <div className="font-clash text-sm font-medium text-white">{p.label}</div>
                                <div className={`text-[10px] font-mono-acc uppercase tracking-[0.18em] ${tone.color}`}>
                                  {tone.label}
                                </div>
                              </div>
                            </div>
                            <div className="font-clash text-xl font-semibold text-white">{p.score}</div>
                          </div>
                          <PillarBar score={p.score} />
                          {p.summary && (
                            <p className="mt-3 text-xs text-zinc-400 leading-relaxed line-clamp-3">
                              {p.summary}
                            </p>
                          )}
                          {!unlocked ? (
                            <div className="mt-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 flex items-center gap-2 text-[11px] text-zinc-500">
                              <Lock size={11} className="text-purple-300" />
                              <span className="select-none blur-[3px] text-zinc-300">3 specific actions hidden</span>
                            </div>
                          ) : p.actions?.length > 0 ? (
                            <ul className="mt-3 space-y-1.5">
                              {p.actions.slice(0, 3).map((a, idx) => (
                                <li key={idx} className="flex items-start gap-1.5 text-[12px] text-zinc-200 leading-snug">
                                  <CheckCircle2 size={11} className="text-cyan-300 mt-0.5 shrink-0" />
                                  <span>{a}</span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Locked: lead-capture gate */}
                  {!unlocked && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-fuchsia-500/10 border border-white/[0.08] p-5 md:p-6"
                      data-testid="audit-unlock-card"
                    >
                      <div className="flex items-center gap-2 text-[11px] font-mono-acc uppercase tracking-[0.25em] text-cyan-300 mb-3">
                        <Lock size={12} />
                        Unlock the full report + PDF
                      </div>
                      <p className="text-sm text-zinc-200 leading-relaxed max-w-xl">
                        Enter your email to instantly reveal the <b>18 specific action items</b> (3 per pillar), the
                        <b> 3 quick wins</b> you can ship this week, and download a branded PDF of your audit.
                      </p>
                      <form onSubmit={onUnlock} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="audit-unlock-form">
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name (optional)"
                          className="sm:max-w-[180px] bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400/50 text-white placeholder:text-zinc-500"
                          data-testid="audit-unlock-name"
                        />
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@yourbrand.com"
                          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400/50 text-white placeholder:text-zinc-500"
                          data-testid="audit-unlock-email"
                        />
                        <button
                          type="submit"
                          disabled={unlockLoading}
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black px-5 py-3 text-sm font-medium hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-60"
                          data-testid="audit-unlock-submit"
                        >
                          {unlockLoading ? (
                            <>
                              <Loader2 size={14} className="animate-spin" /> Unlocking…
                            </>
                          ) : (
                            <>
                              Reveal full report
                              <ArrowRight size={14} />
                            </>
                          )}
                        </button>
                      </form>
                      <p className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1.5">
                        <Mail size={10} /> We'll only contact you with your audit + a personal note from our team. No spam.
                      </p>
                    </motion.div>
                  )}

                  {/* Unlocked: quick wins + PDF */}
                  {unlocked && displayQuickWins?.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-fuchsia-500/10 border border-white/[0.08] p-5"
                      data-testid="audit-quick-wins"
                    >
                      <div className="flex items-center gap-2 text-[11px] font-mono-acc uppercase tracking-[0.25em] text-cyan-300 mb-3">
                        <AlertTriangle size={12} />
                        Quick wins to ship this week
                      </div>
                      <ul className="space-y-2">
                        {displayQuickWins.map((q, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-zinc-100">
                            <span className="font-mono-acc text-cyan-300 text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                            <span className="leading-relaxed">{q}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-5 flex flex-col sm:flex-row gap-2">
                        {pdfHref && (
                          <a
                            href={pdfHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-xs font-medium hover:scale-[1.03] transition-transform"
                            data-testid="audit-download-pdf"
                          >
                            <Download size={13} />
                            Download PDF report
                          </a>
                        )}
                        <a
                          href="#contact"
                          className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-5 py-2.5 text-xs hover:bg-white/10 transition-colors"
                          data-testid="audit-cta-strategy-call"
                        >
                          Turn this into a growth plan
                          <ArrowRight size={13} />
                        </a>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
